<script lang="ts">
	//Svelte
	import { onMount, setContext } from 'svelte';
	import { writable } from 'svelte/store';
	//Services
	import { format } from 'svelte-i18n';
	import { postReq } from 'src/services/http';
	//Stores
	import { resetEditUpdateStore, update } from 'src/stores/ui/edit_lists';
	//Helpers
	import { capFirstChar } from 'src/helpers/utilities';
	import { getDeviceRoomList, getMappedRoomList } from '../Lists/list-helpers';
	//Types
	import type { Device } from 'src/types/Device';
	import type { Writable } from 'svelte/store';
	//Design System Components
	import { Button, Container, Modal, Text, toast } from '@Shelly/shelly-design-system';
	//Components
	import SelectList from '../SelectList/SelectList.svelte';
	import CreateNewRoom from 'src/components/Lists/Rooms/CreateNewRoom.svelte';
	import { push } from 'svelte-spa-router';

	// Types
	interface Room {
		id: number | null;
		name: string;
	}

	interface NewRoomStore {
		newRoom: any;
		newRoomEditing: boolean;
	}

	interface DeviceUpdate {
		[deviceId: string]: Device & { room_id: number };
	}

	// Props
	export let showModal: boolean;
	export let areFiltersDisabled: boolean;
	export let currentRoomId: number | boolean;

	// State
	let isLoading = false;
	let isMounted = false;
	let deviceRoomListDB: any[] = [];
	let selectedRoom: Room = {
		id: null,
		name: '',
	};
	let allRoomsRefs: Record<string | number, HTMLElement> = {};

	// Store setup
	const createNewRoomStore: Writable<NewRoomStore> = writable({
		newRoom: undefined,
		newRoomEditing: false,
	});

	setContext('newRoomStore', createNewRoomStore);

	// Reactive statements
	$: selectedDevices = Object.values(
		$update.selectedDevices as {
			[key: string]: Device;
		},
	) as Device[];

	$: ({ newRoomEditing } = $createNewRoomStore);
	$: deviceRoomListMapped = getMappedRoomList(deviceRoomListDB, currentRoomId);
	$: selectedRoom = {
		id: selectedDevices[0].room_id,
		name: '',
	};
	$: isSubmitDisabled = !selectedRoom.id || selectedDevices.length === 0;
	$: modalTitle = capFirstChar($format(`sub_settings_title_${$update.type}`));

	// Helper functions
	const resetState = (): void => {
		resetEditUpdateStore();

		$createNewRoomStore = {
			newRoom: undefined,
			newRoomEditing: false,
		};

		selectedRoom = { id: null, name: '' };
		allRoomsRefs = {};
	};

	const createDeviceUpdateObject = (devices: Device[], roomId: number): DeviceUpdate => {
		return devices.reduce((acc, device) => {
			acc[device.id] = { ...device, room_id: roomId };
			return acc;
		}, {} as DeviceUpdate);
	};

	// Event handlers
	const submit = async (): Promise<void> => {
		if (!selectedRoom.id || selectedDevices.length === 0) {
			return;
		}

		isLoading = true;

		try {
			const editSelectedDevicesToObject = createDeviceUpdateObject(
				selectedDevices,
				selectedRoom.id,
			);

			const response = await postReq('interface/device/bulk_update', {
				devices_data: JSON.stringify(editSelectedDevicesToObject),
			});

			if (response.isok) {
				toast.open({
					type: 'success',
					title: $format('text_successful_transfer', {
						default: 'Successfully transferred devices',
					}),
				});

				push(`/home/room/${selectedRoom.id}/devices`); // Navigate to the new room's devices page. Also works with "Hidden devices" room
				resetState();
			} else {
				throw new Error('Transfer failed');
			}
		} catch (error) {
			console.error('Error transferring devices:', error);
			toast.open({
				type: 'danger',
				title: $format('text_failed_transfer', {
					default: 'Failed to transfer devices',
				}),
			});
		} finally {
			isLoading = false;
			resetEditUpdateStore();
			areFiltersDisabled = false;
			showModal = false;
		}
	};

	const onClose = (): void => {
		showModal = false;
		resetState();
	};

	const onSelectRoom = (room: Room): void => {
		selectedRoom = room;
	};

	const handleAfterRoomUpdate = (): void => {
		if (isMounted) {
			deviceRoomListDB = getDeviceRoomList();
		}
	};

	// Lifecycle
	onMount(() => {
		deviceRoomListDB = getDeviceRoomList();
		isMounted = true;
	});

	// Reactive effects
	$: if (!newRoomEditing && isMounted) {
		deviceRoomListDB = getDeviceRoomList();
	}
</script>

<Modal on:close={onClose} {showModal} title={modalTitle}>
	<Container slot="content" fullHeight={newRoomEditing} padding="0">
		<Text element="h2" class="mb-m">
			{$format('text_modal_select_switchable_room', {
				default: 'Select a room to transfer devices to',
			})}
		</Text>

		{#if newRoomEditing}
			<CreateNewRoom on:after-room-update={handleAfterRoomUpdate} />
		{:else}
			<SelectList
				list={deviceRoomListMapped}
				variant="radio"
				selectedCardsId={[{ id: selectedRoom.id }]}
				bind:allCardsRefs={allRoomsRefs}
				on:click={(e) => onSelectRoom(e.detail)}
			/>
		{/if}
	</Container>

	<div slot="footer">
		{#if !newRoomEditing}
			<div class="d-flex justify-content-end">
				<Button
					variant="secondary"
					class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
					on:click={onClose}
				>
					{$format('button_cancel', { default: 'Cancel' })}
				</Button>
				<Button
					loading={isLoading}
					disabled={isSubmitDisabled}
					class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
					on:click={submit}
				>
					{$format('button_transfer', { default: 'Transfer' })}
				</Button>
			</div>
		{/if}
	</div>
</Modal>
